import { expectBoolean, expectNullable, expectNumber, expectRecord, expectString } from "./schema";
export const STUDENT_BILLING_PAYMENT_ACCOUNT_STATUSES = [
    "notStarted",
    "onboardingPending",
    "restricted",
    "ready",
    "disabled"
];
export const STUDENT_BILLING_CARD_PAYMENTS_CAPABILITY_STATUSES = [
    "unavailable",
    "pending",
    "restricted",
    "unsupported",
    "active"
];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function enumValue(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function nullableTimestamp(value, path) {
    const parsed = expectNullable(value, (item, itemPath) => expectString(item, itemPath), path);
    if (parsed !== null && Number.isNaN(Date.parse(parsed)))
        throw new Error(`${path} must be an ISO timestamp or null`);
    return parsed;
}
export function parseStudentBillingPaymentAccountCapabilitiesDto(value, path = "studentBillingPaymentAccountCapabilities") {
    const record = expectRecord(value, path);
    exact(record, ["canReadPaymentAccount", "canManagePaymentAccount"], path);
    return {
        canReadPaymentAccount: expectBoolean(record.canReadPaymentAccount, `${path}.canReadPaymentAccount`),
        canManagePaymentAccount: expectBoolean(record.canManagePaymentAccount, `${path}.canManagePaymentAccount`)
    };
}
export function parseStudentBillingPaymentAccountDto(value, path = "studentBillingPaymentAccount") {
    const record = expectRecord(value, path);
    exact(record, [
        "status",
        "chargesAvailable",
        "payoutsAvailable",
        "onboardingComplete",
        "cardPaymentsCapability",
        "attentionRequired",
        "lastCheckedAt",
        "updatedAt",
        "capabilities"
    ], path);
    return {
        status: enumValue(record.status, STUDENT_BILLING_PAYMENT_ACCOUNT_STATUSES, `${path}.status`),
        chargesAvailable: expectBoolean(record.chargesAvailable, `${path}.chargesAvailable`),
        payoutsAvailable: expectBoolean(record.payoutsAvailable, `${path}.payoutsAvailable`),
        onboardingComplete: expectBoolean(record.onboardingComplete, `${path}.onboardingComplete`),
        cardPaymentsCapability: enumValue(record.cardPaymentsCapability, STUDENT_BILLING_CARD_PAYMENTS_CAPABILITY_STATUSES, `${path}.cardPaymentsCapability`),
        attentionRequired: expectBoolean(record.attentionRequired, `${path}.attentionRequired`),
        lastCheckedAt: nullableTimestamp(record.lastCheckedAt, `${path}.lastCheckedAt`),
        updatedAt: nullableTimestamp(record.updatedAt, `${path}.updatedAt`),
        capabilities: parseStudentBillingPaymentAccountCapabilitiesDto(record.capabilities, `${path}.capabilities`)
    };
}
export function parseStudentBillingConnectOnboardingSessionDto(value, path = "studentBillingConnectOnboardingSession") {
    const record = expectRecord(value, path);
    exact(record, ["onboardingUrl", "expiresAt"], path);
    const onboardingUrl = expectString(record.onboardingUrl, `${path}.onboardingUrl`);
    const expiresAt = expectString(record.expiresAt, `${path}.expiresAt`);
    const parsedUrl = new URL(onboardingUrl);
    if (parsedUrl.protocol !== "https:")
        throw new Error(`${path}.onboardingUrl must use HTTPS`);
    if (Number.isNaN(Date.parse(expiresAt)))
        throw new Error(`${path}.expiresAt must be an ISO timestamp`);
    return { onboardingUrl, expiresAt };
}
export function parseStudentBillingPaymentAccountMutationRequest(value) {
    const record = expectRecord(value, "studentBillingPaymentAccountMutation");
    exact(record, [], "studentBillingPaymentAccountMutation");
    return {};
}
export function assertStudentBillingPaymentAccountVersion(value, path = "authorityVersion") {
    const parsed = expectNumber(value, path);
    if (!Number.isSafeInteger(parsed) || parsed < 1)
        throw new Error(`${path} must be a positive integer`);
    return parsed;
}
